import { isSimpleRecurrence, sortTasksByOpenAndDate, type TaskFilterType } from '@frontend/api-client';
import { useFeatureValue } from '@frontend/react';
import type { TaskTabObject } from '@frontend/react-domain';
import {
  filterTaskLikeObjectsByTabType,
  getVisibleTaskFilters,
  handleSelectFilter,
  joinTaskLikeObjects,
} from '@frontend/react-domain';
import { FlatList, PageContainer, Spinner, XStack, YStack } from '@frontend/web-react';
import { concatClasses } from '@frontend/web-utils';
import { EmptyTask, TaskFilterButton, TasksHeader } from '@frontend/web/components';
import { TaskLikeCard } from '@frontend/web/components/DomainSpecific/Tasks/TaskLikeCard';
import { useListDraftTasksQuery, useListRecurrencesQuery, useListTasksQuery } from '@frontend/web/hooks';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

const Tasks = () => {
  const { data: tasks = [], isLoading: isListTasksLoading } = useListTasksQuery();
  const { data: draftTasks = [], isLoading: isDraftTasksLoading } = useListDraftTasksQuery();
  const { data: recurrences = [] } = useListRecurrencesQuery();
  const [activeFilters, setActiveFilters] = useState<TaskFilterType[]>([]);
  const { value: markAsDoneDeployDate } = useFeatureValue('mark_as_done_deploy_date');

  const isLoading = isListTasksLoading || isDraftTasksLoading;

  const taskLikeObjects = useMemo(() => {
    const filtered = filterTaskLikeObjectsByTabType({
      markAsDoneDeployDate,
      taskFilters: activeFilters,
      taskLikeObjects: joinTaskLikeObjects({ draftTasks, tasks }),
    });

    return sortTasksByOpenAndDate(filtered);
  }, [activeFilters, tasks, draftTasks, markAsDoneDeployDate]);

  const nextTriggerByTaskId = Object.fromEntries(
    recurrences
      .filter(isSimpleRecurrence)
      .filter((r) => r.taskId && r.nextTriggerAt)
      .map((r) => [r.taskId, r.nextTriggerAt as DateTimeISOString]),
  );

  const isEmpty = !taskLikeObjects.length && !isLoading;

  const visibleFilters: TaskTabObject[] = useMemo(
    () => getVisibleTaskFilters(tasks, draftTasks, markAsDoneDeployDate),
    [tasks, draftTasks, markAsDoneDeployDate],
  );

  const onChangeFilter = (filter: TaskFilterType) => {
    setActiveFilters((prev) =>
      handleSelectFilter({
        activeFilters: prev,
        selectedFilter: filter,
      }),
    );
  };

  return (
    <PageContainer
      className="flex-1"
      innerContainerClassName="max-h-[100%] px-md overflow-x-hidden"
      isLoading={isLoading}
      isScrollable={false}
      padding="none"
    >
      <YStack className="w-full items-center py-sm lg:py-lg">
        <YStack className="max-w-screen-lg w-full">
          <YStack className={concatClasses('w-full gap-md')}>
            <TasksHeader />
            <XStack className="gap-sm py-md flex-row overflow-x-auto flex-nowrap no-scroll-bar">
              {visibleFilters.map((item) => (
                <TaskFilterButton
                  isActive={activeFilters.includes(item.key)}
                  key={item.key}
                  onClick={() => onChangeFilter(item.key)}
                  title={item.title}
                />
              ))}
            </XStack>
          </YStack>
        </YStack>
      </YStack>
      <FlatList
        ListEmptyComponent={<EmptyTask type={isEmpty ? 'no_filtered_task' : 'no_task'} />}
        ListLoadingComponent={<Spinner color="neutral.9" />}
        accessibilityLabel="Tasks list"
        className="w-full items-center"
        contentContainerClassName="gap-sm max-w-screen-lg w-full pb-md"
        data={taskLikeObjects}
        isLoading={isListTasksLoading}
        renderItem={({ index, item: task }) => (
          <TaskLikeCard
            animationIndex={index}
            isAnimated
            nextTriggerAt={nextTriggerByTaskId[task.id]}
            showInlineActions
            taskLikeObject={task}
          />
        )}
      />
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/task/')({
  component: Tasks,
});
